import { useEffect, useMemo, useState } from 'react'
import type { NwsGeometry } from '@/lib/nws/types'
import { estimateEta, geometryCentroid, type Eta, type LatLon } from '@/lib/storm/geo'
import { advanceMotion, type StormMotion } from '@/lib/storm/motion'

/** Cap how far we extrapolate a fixed issue-time vector (cells curve / weaken). */
const MAX_ADVANCE_MS = 30 * 60_000

export interface LiveStorm {
  eta: Eta
  /** The motion advanced to "now" — share this with the map so dot/arrow match the ETA. */
  motion: StormMotion | null
}

/**
 * Live ETA: re-projects the storm's motion vector to "now" every 20s so the
 * "arrives in ~N min" countdown ticks down as the cell tracks toward the user — and
 * returns the same advanced vector so the radar dot/arrow never drift out of sync.
 */
export function useLiveEta(
  user: LatLon,
  motion: StormMotion | null,
  geometry: NwsGeometry | null,
  userInside: boolean,
): LiveStorm {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 20_000)
    return () => clearInterval(t)
  }, [])

  return useMemo(() => {
    // Seed a reference point from the polygon centroid when the vector carries no LOC
    // coords, so the cell still advances over time instead of freezing on a static point.
    let base = motion
    if (base && !base.points.length) {
      const gc = geometryCentroid(geometry)
      if (gc) base = { ...base, points: [gc] }
    }
    const rawSince = base?.at ? now - Date.parse(base.at) : 0
    const sinceMs = Math.min(Math.max(0, Number.isFinite(rawSince) ? rawSince : 0), MAX_ADVANCE_MS)
    const advanced = base && sinceMs > 0 ? advanceMotion(base, sinceMs) : base
    return { eta: estimateEta(user, advanced ?? null, geometry, userInside), motion: advanced ?? null }
  }, [user, motion, geometry, userInside, now])
}
